import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type { Group } from './groups_slice';
import { navigateTo } from '../../_config/history.config';
import axiosInstance from '../../_helpers/axios_instance';
import { logEvent } from '../../_helpers/UtilityReactComponents/UtilizationTracking';
import { setContentFilters, dispatchLogEventRewards } from './enrichment_center_slice';
import { updateRewardSettings } from './tremendous_slice';
import { getDefaultECFilters, isDemoEnv, showBurnoutQuestions } from '../../_helpers/misc_functions';


export interface User {
  id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  /** The API's admin role ('app_admin' etc.), separate from burnout_role. */
  role?: string;
  burnout_role?: string;
  practice_group_ids?: number[];
  practice_group_id?: number;
  // Both are serialized with PracticeGroupSerializer. A user reaches at most one
  // group (practice_providers -> practices -> practice_groups), so practice_groups
  // holds either nothing or a single entry.
  practice_group?: Group;
  practice_groups?: Group[];
  last_burnout_level_response?: unknown;
  last_burnout_stressor_response?: unknown;
  /** The latest check-in's stressor categories, by rank. */
  latest_stressor_categories?: Array<{ id?: number; name: string; description?: string; enabled?: boolean }>;
  /** Every stressor the user has named in a check-in, latest picks first. */
  named_stressors?: string[];
  profile_picture_url?: string;
  sign_in_count?: number;
  /** "MM/DD/YYYY", or "" when no date of birth is on file. */
  dob_string?: string;
  state?: string | null;
  license_id?: string | null;
  license_type?: 'physician' | 'nurse' | 'advanced_practice_provider' | 'other' | null;
  [key: string]: unknown;
}

export interface AuthenticationState {
  requestPending: boolean;
  requestError: string | string[] | null;
  checkingConfirmationToken: boolean;
  confirmationValid?: boolean;
  successMessage: string | null;
  user: User;
  accessToken: string | null;
  client: string | null;
  email: string | null;
  expiry: string | null;
  user_id: number | null;
  validatingToken: boolean;
}

const defaultLocation = { pathname: '/', search: '', hash: '', state: {} };

const emptyState = (): AuthenticationState => ({
  requestPending: false,
  requestError: null,
  checkingConfirmationToken: false,
  successMessage: null,
  user: {},
  accessToken: null,
  client: null,
  email: null,
  expiry: null,
  user_id: null,
  validatingToken: false,
});

// Devise Token Auth sends `expiry` as a Unix timestamp in *seconds*.
// Only report expiry when we can positively determine it: a missing or
// unparseable value keeps the session, so flows that never set one (OAuth)
// aren't signed out on every page load.
export const isSessionExpired = (expiry: unknown): boolean => {
  if (expiry === null || expiry === undefined || expiry === '') return false;
  const seconds = Number(expiry);
  if (!Number.isFinite(seconds)) return false;
  return seconds * 1000 <= Date.now();
};

const loadInitialState = (): AuthenticationState => {
  const existingSession = JSON.parse(localStorage.getItem('currentSession') ?? 'null');
  if (existingSession) {
    // Rehydrating an expired token leaves `loggedIn` true (it only checks that
    // `user` is non-empty), so RequireAuth would boot straight into the app and
    // fire API calls the backend rejects. Drop the session instead and let the
    // user land on /login.
    if (isSessionExpired(existingSession.expiry)) {
      localStorage.removeItem('currentSession');
      localStorage.removeItem('currentBlock');
      localStorage.removeItem('currentGroup');
      return emptyState();
    }
    return Object.assign(existingSession, {
      requestPending: false,
      requestError: null,
      checkingConfirmationToken: false,
      successMessage: null,
      validatingToken: false,
    });
  }
  return emptyState();
};

type AuthThunkConfig = { state: RootState };

export const login = createAsyncThunk<any, { email: string; password: string; location?: typeof defaultLocation }, AuthThunkConfig>(
  'authentication/login',
  async ({ email, password, location = defaultLocation }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/sign_in', { uid: email, password });
      const logResult = await logEvent('logged in', null, null, {
        accessToken: response.headers['access-token'],
        email: response.headers.uid,
        client: response.headers.client,
      });
      dispatch(dispatchLogEventRewards(logResult));
      localStorage.setItem('currentSession', JSON.stringify({
        user: response.data?.data,
        email: response.headers.uid,
        accessToken: response.headers['access-token'],
        client: response.headers.client,
        expiry: response.headers.expiry,
      }));
      if (isDemoEnv()) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('show_level', 'true');
        searchParams.set('show_stressor', 'true');
        // preserve the intended destination so completing the assessment returns the user there.
        // Normalize the leading '?' here too: this is raw string concatenation, so a caller
        // passing a bare search would otherwise yield '/email=...' with no separator.
        const rawSearch = location.search || '';
        const search = !rawSearch || rawSearch.startsWith('?') ? rawSearch : `?${rawSearch}`;
        searchParams.set('redirect_url', `${location.pathname || '/'}${search}`);
        navigateTo({ pathname: '/assessment', search: `?${searchParams.toString()}` } as any);
      } else {
        navigateTo(location as any);
      }
      dispatch(setContentFilters(getDefaultECFilters(response.data)));
      return {
        user: response.data?.data,
        email: response.headers.uid,
        accessToken: response.headers['access-token'],
        client: response.headers.client,
        expiry: response.headers.expiry,
        logResult,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

export const checkConfirmationToken = createAsyncThunk<any, { token: string; email: string }, AuthThunkConfig>(
  'authentication/checkConfirmationToken',
  async ({ token, email }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/users/by_token', {
        params: { token, email },
      });
      return { email: response.data.email, user_id: response.data.id };
    } catch (error: any) {
      navigateTo('/login');
      return rejectWithValue(error.response?.data?.message || 'Invalid Confirmation Link');
    }
  },
  { condition: (_, { getState }) => !getState().authentication.checkingConfirmationToken }
);

export const setPassword = createAsyncThunk<string, any, AuthThunkConfig>(
  'authentication/setPassword',
  async ({ user_id, email, token, password, password_confirmation, location = defaultLocation }, { rejectWithValue }) => {
    try {
      await axiosInstance.put(`/api/users/${user_id}`, { confirmation_token: token, email, password, password_confirmation });
      const searchParams = new URLSearchParams(location.search || '');
      searchParams.set('email', email);
      searchParams.set('first_login', 'true');
      navigateTo(`/login?${searchParams.toString()}${location.hash || ''}`, true, location.state);
      return 'Your account has been confirmed!\n\rYou may now login.';
    } catch (error: any) {
      const errors: string[] = [];
      if (error.response?.data?.errors?.password) errors.push('Password ' + error.response.data.errors.password[0]);
      if (error.response?.data?.errors?.password_confirmation) errors.push('Password Confirmation ' + error.response.data.errors.password_confirmation[0]);
      return rejectWithValue(errors.length ? errors : error.message);
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

export const updateUser = createAsyncThunk<User, { user_id: number; data: unknown }, AuthThunkConfig>(
  'authentication/updateUser',
  async ({ user_id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/providers/${user_id}`, data);
      localStorage.setItem('currentSession', JSON.stringify({
        user: response.data, email: response.headers.uid,
        accessToken: response.headers['access-token'], client: response.headers.client, expiry: response.headers.expiry,
      }));
      dispatch(setContentFilters(getDefaultECFilters(response.data)));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  }
);

export const uploadProfilePicture = createAsyncThunk<User, { user_id: number; file: File }, AuthThunkConfig>(
  'authentication/uploadProfilePicture',
  async ({ user_id, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('profile_picture', file);
      const response = await axiosInstance.put(`/api/providers/${user_id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      localStorage.setItem('currentSession', JSON.stringify({
        user: response.data, email: response.headers.uid,
        accessToken: response.headers['access-token'], client: response.headers.client, expiry: response.headers.expiry,
      }));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  }
);

export const requestPasswordReset = createAsyncThunk<string, { email: string }, AuthThunkConfig>(
  'authentication/requestPasswordReset',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/auth/password`,
        { email, redirect_url: `${process.env.REACT_APP_BASE_URL}/reset-password` }
      );
      navigateTo('/login');
      return response.data.message;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

export const validateToken = createAsyncThunk<unknown, { credentials: { email: string; accessToken: string; client: string } }, AuthThunkConfig>(
  'authentication/validateToken',
  async ({ credentials }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_URL}/auth/validate_token`, {
        headers: { uid: credentials.email, 'access-token': credentials.accessToken, client: credentials.client },
      });
      return response.data;
    } catch {
      navigateTo('/login');
      return rejectWithValue('Password Reset Link has expired');
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

export const resetPassword = createAsyncThunk<string, any, AuthThunkConfig>(
  'authentication/resetPassword',
  async ({ credentials, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        '/auth/password',
        { email: credentials.email, password, password_confirmation },
        { headers: { uid: credentials.email, 'access-token': credentials.accessToken, client: credentials.client } }
      );
      navigateTo('/login');
      return response.data.message;
    } catch (error: any) {
      const errors: string[] = [];
      if (error.response?.data?.errors?.password) errors.push('Password ' + error.response.data.errors.password[0]);
      if (error.response?.data?.errors?.password_confirmation) errors.push('Password Confirmation ' + error.response.data.errors.password_confirmation[0]);
      return rejectWithValue(errors.length ? errors : error.message);
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

export const createUser = createAsyncThunk<string, any, AuthThunkConfig>(
  'authentication/createUser',
  async ({ first_name, last_name, email, specialty, gender, dob, license_type, organization, practice_id, block_id = null, years_in_profession = null }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${process.env.REACT_APP_API_URL}/api/create_burnout_user`, {
        enabled: true, practice_ids: [practice_id], is_provider: true, ce_eligible: true,
        provider_title: license_type, first_name, last_name, email, burnout_role: 'physician',
        dob_string: dob, years_in_profession, gender, burnout_location: organization, burnout_specialty: specialty, block_id,
      });
      const { confirmation_token } = response.data;
      navigateTo(`/confirm-password/${confirmation_token}/${email.toLowerCase()}`);
      return `A confirmation email has been sent to ${email} with a link to set your initial password.`;
    } catch (error: any) {
      if (error.response?.data?.errors?.email) {
        return rejectWithValue('An account is already registered with that email.');
      }
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

// Encoded so a "+" in the address survives the login page's query parsing.
const loginPathFor = (email: string) => `/login?email=${encodeURIComponent(email)}`;

export const createUserWithCode = createAsyncThunk<string, any, AuthThunkConfig>(
  'authentication/createUserWithCode',
  async ({ first_name, last_name, email, specialty, gender, dob, license_type, department, years_in_profession, code }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/create_burnout_user_with_code', {
        provider_title: license_type, first_name, last_name, email, burnout_role: 'physician',
        dob_string: dob, years_in_profession, gender, burnout_location: department, burnout_specialty: specialty, code,
      });
      const { confirmation_token } = response.data;
      navigateTo(`/confirm-password/${confirmation_token}/${email.toLowerCase()}`);
      return `A confirmation email has been sent to ${email} with a link to set your initial password.`;
    } catch (error: any) {
      const refusal = error.response?.data;
      // The address has an account still waiting to be confirmed: the API refused
      // the sign-up but re-sent that account's confirmation email, so registration
      // is over. Go to log in and show the API's message there as a success. Left
      // on the form as an error, it read like a failure and invited another submit,
      // and every submit sends another email.
      if (refusal?.data?.confirmation_email_sent) {
        navigateTo(loginPathFor(email));
        return refusal.data.message;
      }
      if (refusal?.errors?.email) {
        return rejectWithValue('An account is already registered with that email.');
      }
      return rejectWithValue(refusal?.errors || error.message);
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

export const billUser = createAsyncThunk<string, any, AuthThunkConfig>(
  'authentication/billUser',
  async ({ code, card_number, expiration_year, expiration_month, card_code, first_name, last_name, email, specialty, gender, dob, license_type, organization, practice_id, block_id = null }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${process.env.REACT_APP_API_URL}/api/register`, {
        enabled: true, practice_ids: [practice_id], is_provider: true, ce_eligible: true,
        provider_title: license_type, first_name, last_name, email, burnout_role: 'physician',
        dob_string: dob, gender, burnout_location: organization, burnout_specialty: specialty,
        block_id, code, card_number, expiration_year, expiration_month, card_code,
      });
      const { confirmation_token } = response.data;
      navigateTo(`/confirm-password/${confirmation_token}/${email.toLowerCase()}`);
      return `A confirmation email has been sent to ${email} with a link to set your initial password.`;
    } catch (error: any) {
      const refusal = error.response?.data;
      // As in createUserWithCode: nothing was charged, and the confirmation email is on its way.
      if (refusal?.data?.confirmation_email_sent) {
        navigateTo(loginPathFor(email));
        return refusal.data.message;
      }
      // The address already has a confirmed account: the API's message says to log
      // in, so go there with it. (errors.email is the shape an older API answered.)
      if (refusal?.data?.account_status === 'confirmed' || refusal?.errors?.email) {
        navigateTo(loginPathFor(email));
      }
      if (refusal?.errors?.email) {
        return rejectWithValue('An account has already been setup with that email');
      }
      return rejectWithValue(refusal?.errors || error.message);
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

export const getUser = createAsyncThunk<User, { id?: number | null; redirect?: string | null } | void, AuthThunkConfig>(
  'authentication/getUser',
  async (args, { getState, rejectWithValue }) => {
    const { id = null, redirect = null } = (args ?? {}) as { id?: number | null; redirect?: string | null };
    const user_id = id || getState().authentication.user?.id;
    if (!user_id) return rejectWithValue('no user id found');
    try {
      const response = await axiosInstance.get(`/api/users/${user_id}`);
      localStorage.setItem('currentSession', JSON.stringify({
        user: response.data, email: response.headers.uid,
        accessToken: response.headers['access-token'], client: response.headers.client, expiry: response.headers.expiry,
      }));
      navigateTo(redirect || defaultLocation as any);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors) &&
        (error.response.data.errors.includes('Authorized users only.') ||
          error.response.data.errors.includes('You need to sign in or sign up before continuing.'))) {
        return rejectWithValue({ sessionExpired: true, message: 'Your session has timed out, please log back in.' });
      }
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  { condition: (_, { getState }) => !getState().authentication.requestPending }
);

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState: loadInitialState(),
  reducers: {
    logout: (state, action: PayloadAction<string | undefined>) => {
      state.requestError = action.payload ?? null;
      state.user = {};
      state.successMessage = null;
      // The root reducer resets state to undefined on logout, which falls back
      // to the initialState captured at module load - i.e. the rehydrated
      // session. Clear the credentials explicitly so a dead token can't be
      // attached to requests made later in the same page session.
      state.accessToken = null;
      state.client = null;
      state.email = null;
      state.expiry = null;
    },
    dismissError: (state) => { state.requestError = null; },
    dismissSuccess: (state) => { state.successMessage = null; },
    updateAccessToken: (state, action: PayloadAction<{ accessToken: string; client: string; email: string; expiry: string }>) => {
      const { accessToken, client, email, expiry } = action.payload;
      state.accessToken = accessToken;
      state.client = client;
      state.email = email;
      state.expiry = expiry;
    },
    authenticationError: (state, action: PayloadAction<string | string[]>) => {
      state.requestError = action.payload;
      state.successMessage = null;
    },
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
      state.requestError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.requestPending = true; state.requestError = null; })
      .addCase(login.fulfilled, (state, action) => {
        state.requestPending = false; state.requestError = null;
        state.user = action.payload.user; state.email = action.payload.email;
        state.accessToken = action.payload.accessToken; state.client = action.payload.client; state.expiry = action.payload.expiry;
        const session = JSON.parse(localStorage.getItem('currentSession') ?? 'null');
        if (session) {
          session.user = state.user;
          localStorage.setItem('currentSession', JSON.stringify(session));
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.requestPending = false;
        state.requestError = (action.payload as string | string[]) ?? action.error.message ?? null;
        state.successMessage = null;
      });

    builder
      .addCase(checkConfirmationToken.pending, (state) => { state.checkingConfirmationToken = true; state.requestError = null; })
      .addCase(checkConfirmationToken.fulfilled, (state, action) => {
        state.checkingConfirmationToken = false; state.confirmationValid = true; state.requestError = null;
        state.email = action.payload.email; state.user_id = action.payload.user_id;
      })
      .addCase(checkConfirmationToken.rejected, (state, action) => {
        state.checkingConfirmationToken = false;
        state.requestError = (action.payload as string) ?? action.error.message ?? null;
        state.successMessage = null;
      });

    builder
      .addCase(setPassword.pending, (state) => { state.requestPending = true; state.requestError = null; })
      .addCase(setPassword.fulfilled, (state, action) => { state.requestPending = false; state.successMessage = action.payload; state.requestError = null; })
      .addCase(setPassword.rejected, (state, action) => {
        state.requestPending = false;
        state.requestError = (action.payload as string | string[]) ?? action.error.message ?? null;
        state.successMessage = null;
      });

    builder
      .addCase(updateUser.pending, (state) => { state.requestPending = true; state.requestError = null; })
      .addCase(updateUser.fulfilled, (state, action) => { state.requestPending = false; state.user = action.payload; })
      .addCase(updateUser.rejected, (state, action) => {
        state.requestPending = false;
        state.requestError = (action.payload as string | string[]) ?? action.error.message ?? null;
        state.successMessage = null;
      });

    builder
      .addCase(uploadProfilePicture.pending, (state) => { state.requestPending = true; state.requestError = null; })
      .addCase(uploadProfilePicture.fulfilled, (state, action) => { state.requestPending = false; state.user = action.payload; })
      .addCase(uploadProfilePicture.rejected, (state, action) => {
        state.requestPending = false;
        state.requestError = (action.payload as string | string[]) ?? action.error.message ?? null;
      });

    builder
      .addCase(requestPasswordReset.pending, (state) => { state.requestPending = true; state.requestError = null; })
      .addCase(requestPasswordReset.fulfilled, (state, action) => { state.requestPending = false; state.successMessage = action.payload; state.requestError = null; })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.requestPending = false;
        state.requestError = (action.payload as string | string[]) ?? action.error.message ?? null;
        state.successMessage = null;
      });

    builder
      .addCase(validateToken.pending, (state) => { state.validatingToken = true; })
      .addCase(validateToken.fulfilled, (state) => { state.validatingToken = false; state.requestError = null; })
      .addCase(validateToken.rejected, (state, action) => {
        state.validatingToken = false;
        state.requestError = (action.payload as string) ?? action.error.message ?? null;
        state.successMessage = null;
      });

    builder
      .addCase(resetPassword.pending, (state) => { state.requestPending = true; state.requestError = null; })
      .addCase(resetPassword.fulfilled, (state, action) => { state.requestPending = false; state.successMessage = action.payload; state.requestError = null; })
      .addCase(resetPassword.rejected, (state, action) => {
        state.requestPending = false;
        state.requestError = (action.payload as string | string[]) ?? action.error.message ?? null;
        state.successMessage = null;
      });

    const userCreationCases = [createUserWithCode, billUser];
    userCreationCases.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => { state.requestPending = true; state.requestError = null; })
        .addCase(thunk.fulfilled, (state, action) => { state.requestPending = false; state.successMessage = action.payload; state.requestError = null; })
        .addCase(thunk.rejected, (state, action) => {
          state.requestPending = false;
          state.requestError = (action.payload as string | string[]) ?? action.error.message ?? null;
          state.successMessage = null;
        });
    });

    builder
      .addCase(getUser.pending, (state) => { state.requestPending = true; state.requestError = null; })
      .addCase(getUser.fulfilled, (state, action) => { state.requestPending = false; state.requestError = null; state.user = action.payload; })
      .addCase(getUser.rejected, (state, action) => {
        state.requestPending = false;
        const payload = action.payload as any;
        state.requestError = payload?.sessionExpired ? payload.message : (payload ?? action.error.message ?? null);
        state.successMessage = null;
      });

    builder
      .addCase(updateRewardSettings.fulfilled, (state, action) => {
        if (state.user) (state.user as any).rewards_enabled = action.payload.rewards_enabled;
      });

  },
});

export const { logout, dismissError, dismissSuccess, updateAccessToken, authenticationError, setSuccessMessage } = authenticationSlice.actions;
export default authenticationSlice.reducer;

const INACTIVITY_MESSAGE = 'Your session has timed out due to inactivity, please log back in.';

// These two run as background polls, so a dropped connection or a transient 5xx
// is not evidence the session ended - only an auth failure is. Anything else is
// left alone rather than throwing the user back to /login.
const isAuthFailure = (error: any) => error?.response?.status === 401;

export const checkSessionTimeout = () => async (dispatch: any) => {
  try {
    const response = await axiosInstance.get('/api/check_session_timeout');
    if (response.data) dispatch(logout(INACTIVITY_MESSAGE));
  } catch (error: any) {
    // The response interceptor already logs out on a 401; re-dispatching is
    // idempotent and swaps its generic copy for the inactivity wording.
    if (isAuthFailure(error)) dispatch(logout(INACTIVITY_MESSAGE));
  }
};

export const resetSessionTimeout = () => async (dispatch: any) => {
  try {
    await axiosInstance.get('/api/reset_user_clock');
  } catch (error: any) {
    if (isAuthFailure(error)) dispatch(logout(INACTIVITY_MESSAGE));
  }
};

export const getCurrentUser = (authentication: AuthenticationState) => authentication.user;
export const loggedIn = (authentication: AuthenticationState) =>
  authentication.user && Object.getOwnPropertyNames(authentication.user).length > 0;
export const isAdmin = (authentication: AuthenticationState) => authentication.user?.burnout_role?.includes('se_admin');
// The API gates its admin endpoints on role app_admin, not burnout_role se_admin, so /admin does too.
export const isAppAdmin = (authentication: AuthenticationState) => authentication.user?.role === 'app_admin';
export const isOrgLeader = (authentication: AuthenticationState) => authentication.user?.burnout_role?.includes('org_leader');
export const isAnaUser = (authentication: AuthenticationState) =>
  authentication.user?.practice_group_ids?.includes(parseInt(process.env.REACT_APP_ANA_GROUP_ID as string));
export const isAafpUser = (authentication: AuthenticationState) =>
  authentication.user?.practice_group_ids?.includes(parseInt(process.env.REACT_APP_AAFP_GROUP_ID as string));
export const isPrismaUser = (authentication: AuthenticationState) =>
  authentication.user?.practice_group_ids?.includes(parseInt(process.env.REACT_APP_PRISMA_GROUP_ID as string));
export const isCeraVeUser = (authentication: AuthenticationState) =>
  authentication.user?.practice_group_ids?.includes(parseInt(process.env.REACT_APP_CERAVE_GROUP_ID as string));
export const isDemoUser = (authentication: AuthenticationState) =>
  authentication.user?.email?.substring(0, 5) === 'demo_';
export const canSeeLocations = (authentication: AuthenticationState) =>
  (authentication.user?.burnout_role &&
    (authentication.user.burnout_role === 'se_admin' || authentication.user.burnout_role === 'system_admin' || authentication.user.burnout_role === 'org_leader')) ||
  getGroupName(authentication) !== 'CPP';
export const canExcludeComments = (authentication: AuthenticationState) =>
  authentication.user?.burnout_role === 'se_admin' || authentication.user?.burnout_role === 'system_admin' || authentication.user?.burnout_role === 'org_leader';
export const canSeeCompletionStatistics = (authentication: AuthenticationState) =>
  authentication.user?.burnout_role === 'se_admin' || authentication.user?.burnout_role === 'system_admin' || authentication.user?.burnout_role === 'org_leader';
// Who can send leader reminders to their group: the roles that already see
// completion statistics.
export const canSendReminders = (authentication: AuthenticationState) =>
  canSeeCompletionStatistics(authentication);
export const canSeeResults = (authentication: AuthenticationState) =>
  authentication.user?.burnout_role === 'se_admin' ||
  authentication.user?.burnout_role === 'org_leader' ||
  authentication.user?.burnout_role === 'physician_leader';
export const getLogoUrl = (authentication: AuthenticationState): string | null => {
  if (authentication.user?.practice_group) return authentication.user.practice_group.logo_url ?? null;
  if ((authentication.user?.practice_groups?.length ?? 0) > 0) return authentication.user.practice_groups![0].logo_url ?? null;
  return null;
};
// The viewer's own group, off their user record — the source MainPage adopts as
// currentGroup, and the fallback for consumers reading before that settles.
// Mirrors getGroupName / getLogoUrl.
export const getOwnGroup = (authentication: AuthenticationState) => {
  if (authentication.user?.practice_group) return authentication.user.practice_group;
  if ((authentication.user?.practice_groups?.length ?? 0) > 0) return authentication.user.practice_groups![0];
  return null;
};
export const getGroupName = (authentication: AuthenticationState): string | null => {
  if (authentication.user?.practice_group) return authentication.user.practice_group.name ?? null;
  if ((authentication.user?.practice_groups?.length ?? 0) > 0) return authentication.user.practice_groups![0].name ?? null;
  return null;
};
export const showBurnoutAssessment = (authentication: AuthenticationState) => {
  const { last_burnout_level_response, last_burnout_stressor_response } = authentication.user;
  return showBurnoutQuestions(last_burnout_level_response, last_burnout_stressor_response);
};